/**
 * <SubjectBadges> — the overlapping row of subject chips (molecule, L0.1).
 *
 * Mirrors the legacy `ObjectiveSubjectsBadges`: maps `Subject[]` to `<SubjectBadge>`s that
 * overlap slightly (each after the first is pulled left), and shows the text label only when
 * there is a single subject (legacy `tinyMode`). Composes the `<SubjectBadge>` atom + `<Row>`.
 *
 * La pile garde sa largeur : au-delà de `max` éléments (trois par défaut, `SUBJECT_STACK_MAX`), elle
 * n'empile que `max - 1` matières et se ferme d'un « +N » (`<SubjectBadgeOverflow>`), qui chevauche
 * comme une chip. Le texte d'une séance compte avec la même constante (`subjectSummary`).
 * `dimmed` estompe les chips, pas le « +N » : c'est du texte, et l'AA vise le texte.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { SubjectBadge, SubjectBadgeOverflow } from "../../atoms/SubjectBadge"
import { Row } from "../../atoms/containers/Row"
import { dimming } from "../../theme/tokens/effects"
import { SUBJECT_STACK_MAX } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { type UISubject } from "../../../ports/primary/ui/types"

export type SubjectBadgesProps = {
    subjects: UISubject[]
    /**
     * Nommer la matière à côté de son icône. Par défaut, seulement quand il n'y en a qu'une —
     * le comportement historique. `false` ne laisse que la pile d'icônes, pour un appelant qui
     * nomme lui-même ses matières (`<ObjectiveHoursChip>`).
     */
    showLabel?: boolean
    /** Les éléments de la pile, « +N » compris. Défaut `SUBJECT_STACK_MAX` (3). */
    max?: number
    /** Estompe les chips : les matières d'une séance où l'enfant ne sera pas. Défaut `false`. */
    dimmed?: boolean
    testID?: string
}

export function SubjectBadges(props: SubjectBadgesProps) {
    const { subjects, max = SUBJECT_STACK_MAX, dimmed = false, testID } = props

    if (subjects.length === 0) return null

    const stacked = subjects.length <= max ? subjects : subjects.slice(0, max - 1)
    const overflow = subjects.length - stacked.length
    const showLabel = props.showLabel ?? subjects.length === 1

    return (
        <Row gap="0" align="center" testID={testID}>
            {stacked.map((subject, index) => (
                <SubjectBadge
                    key={`${subject.name}-${index}`}
                    subject={subject}
                    showLabel={showLabel}
                    style={[index === 0 ? undefined : styles.overlap, dimmed ? styles.dimmed : undefined]}
                />
            ))}
            {overflow > 0 && <SubjectBadgeOverflow count={overflow} style={styles.overlap} />}
        </Row>
    )
}

const styles = StyleSheet.create(() => ({
    overlap: {
        marginLeft: -4,
    },
    dimmed: {
        opacity: dimming.opacity,
    },
}))
